import React, { memo } from 'react';
import { normalizeVertical, type VerticalType } from '../config/vertical';

type LoadingSpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

interface LoadingSpinnerProps {
  /** Size variant. lg = default (h-12). Use sm/md for inline/button contexts. Full-page: 2xl or 3xl (~50% of 4xl). */
  size?: LoadingSpinnerSize;
  /** Optional className for color (e.g. text-white, text-grape-600). Uses currentColor. */
  className?: string;
  /** Vertical-specific loading graphic. */
  vertical?: VerticalType;
}

const sizeClasses: Record<LoadingSpinnerSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
  xl: 'h-20 w-20',
  '2xl': 'h-32 w-32',
  '3xl': 'h-24 w-24',  // ~50% of 4xl for full-page loading
  '4xl': 'h-48 w-48',
};

function resolveVertical(vertical?: VerticalType): VerticalType {
  if (vertical) return vertical;

  if (typeof window !== 'undefined') {
    const themeVertical = getComputedStyle(document.documentElement)
      .getPropertyValue('--vertical-id')
      .trim();
    if (themeVertical) {
      return normalizeVertical(themeVertical);
    }

    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
    return normalizeVertical(firstSegment);
  }

  return 'vineyard';
}

/** Grape cluster positions (inverted teardrop). Defined once to avoid alloc on every render. */
const GRAPE_COORDS: ReadonlyArray<readonly [number, number]> = [
  [28, 12],
  [22, 18], [28, 17], [34, 18],
  [18, 24], [24, 23], [30, 22], [36, 23],
  [16, 30], [22, 29], [28, 29], [34, 29], [40, 30],
  [18, 36], [24, 35], [30, 35], [36, 35],
  [22, 41], [28, 40], [34, 41],
];

/** Shared SVG props for both grape leaves (DRY, single place to tune). */
const leafPathProps = {
  fill: 'currentColor' as const,
  fillOpacity: 0.9,
  stroke: 'currentColor' as const,
  strokeWidth: 0.6,
  strokeLinejoin: 'miter' as const,
};

/** Blossom cluster positions for orchard graphic. Defined once to avoid alloc on every render. */
const BLOSSOM_COORDS: ReadonlyArray<readonly [number, number]> = [
  [22, 15],
  [34, 15],
  [18, 25],
  [38, 25],
  [28, 20],
];

/** Floating greenhouse: glass house shape with arched roof and soft shadow (greenhouse vertical). */
const FloatingGreenhouseGraphic = memo(function FloatingGreenhouseGraphic({
  size,
  className,
  id,
}: {
  size: LoadingSpinnerSize;
  className: string;
  id: string;
}) {
  return (
    <div className="inline-flex items-center justify-center" aria-label="Loading">
      <svg
        viewBox="0 0 56 52"
        className={`${sizeClasses[size]} ${className}`}
        aria-hidden
      >
        <defs>
          <linearGradient id={`greenhouseGlass-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.08" />
          </linearGradient>
          <filter id={`greenhouseShadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.2" floodColor="rgba(0,0,0,0.2)" floodOpacity="1" />
          </filter>
        </defs>
        {/* Soft shadow suggesting “floating” */}
        <ellipse cx="28" cy="48" rx="14" ry="2.5" fill="rgba(0,0,0,0.12)" />
        {/* Base structure: rectangular greenhouse with arched roof */}
        <g filter={`url(#greenhouseShadow-${id})`} className="text-green-800">
          {/* Back wall / base */}
          <path
            d="M 12 38 L 12 24 L 44 24 L 44 38 Z"
            fill={`url(#greenhouseGlass-${id})`}
            stroke="currentColor"
            strokeWidth="1.2"
            strokeOpacity="0.8"
          />
          {/* Arched roof (front arch) */}
          <path
            d="M 11 24 Q 28 8 45 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          {/* Roof panels */}
          <path
            d="M 12 24 L 28 12 L 44 24"
            fill={`url(#greenhouseGlass-${id})`}
            stroke="currentColor"
            strokeWidth="1"
            strokeOpacity="0.7"
          />
          {/* Door */}
          <path
            d="M 26 38 L 26 28 L 30 28 L 30 38 Z"
            fill="currentColor"
            fillOpacity="0.2"
            stroke="currentColor"
            strokeWidth="0.8"
          />
        </g>
        {/* Subtle spin on a small element to indicate loading */}
        <circle cx="28" cy="12" r="2" fill="currentColor" fillOpacity="0.6" className="animate-pulse" />
      </svg>
    </div>
  );
});

/** Blossoming tree: trunk, branches, and flower clusters (orchard vertical). */
const BlossomingTreeGraphic = memo(function BlossomingTreeGraphic({
  size,
  className,
  id,
}: {
  size: LoadingSpinnerSize;
  className: string;
  id: string;
}) {
  return (
    <div className="inline-flex items-center justify-center" aria-label="Loading">
      <svg
        viewBox="0 0 56 52"
        className={`${sizeClasses[size]} ${className}`}
        aria-hidden
      >
        <defs>
          <radialGradient id={`blossomHighlight-${id}`} cx="0.35" cy="0.35" r="0.7">
            <stop offset="0%" stopColor="white" stopOpacity="0.7" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <filter id={`treeShadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="0.8" floodColor="rgba(0,0,0,0.25)" floodOpacity="1" />
          </filter>
        </defs>
        {/* Ground shadow */}
        <ellipse cx="28" cy="50" rx="12" ry="2" fill="rgba(0,0,0,0.1)" />
        {/* Trunk and branches */}
        <g className="text-amber-900" strokeLinecap="round" strokeLinejoin="round" filter={`url(#treeShadow-${id})`}>
          <path
            d="M 28 50 L 28 32 Q 28 28 26 24 L 28 22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          />
          <path d="M 28 32 Q 32 30 36 26 L 38 24" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M 28 32 Q 24 30 20 26 L 18 24" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M 28 22 L 32 18 L 34 16" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M 28 22 L 24 18 L 22 16" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </g>
        {/* Blossom clusters (small circles with highlight) */}
        <g className="text-blue-200" fill="currentColor">
          {BLOSSOM_COORDS.map(([cx, cy]) => (
            <g key={`${cx}-${cy}`}>
              <circle cx={cx} cy={cy} r="3.2" fill="currentColor" />
              <circle cx={cx} cy={cy} r="3.2" fill={`url(#blossomHighlight-${id})`} />
            </g>
          ))}
        </g>
        {/* Subtle pulse to indicate loading */}
        <circle cx="28" cy="20" r="2.5" className="text-blue-400 animate-pulse" fill="currentColor" fillOpacity="0.8" />
      </svg>
    </div>
  );
});

/**
 * Grape cluster loading indicator: dense teardrop-shaped bunch with stem and lobed vine leaves.
 * Whole cluster ripens in sync; stem and leaves use leaf color. 3D highlight + drop shadow in defs.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = memo(({ size = 'lg', className = 'text-white', vertical }) => {
  const id = React.useId().replace(/:/g, '');
  const resolvedVertical = resolveVertical(vertical);

  if (resolvedVertical === 'greenhouse') {
    return <FloatingGreenhouseGraphic size={size} className={className} id={id} />;
  }
  if (resolvedVertical === 'orchard') {
    return <BlossomingTreeGraphic size={size} className={className} id={id} />;
  }

  return (
    <div className="inline-flex items-center justify-center" aria-label="Loading">
      <svg
        viewBox="0 0 56 52"
        className={`${sizeClasses[size]} ${className}`}
        aria-hidden
      >
        <defs>
          {/* 3D highlight: white-to-transparent overlay so base color (currentColor) shows through and animates */}
          <radialGradient
            id={`grapeHighlight-${id}`}
            cx="0.35"
            cy="0.35"
            r="0.7"
            fx="0.25"
            fy="0.25"
          >
            <stop offset="0%" stopColor="white" stopOpacity="0.65" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          {/* Soft shadow under cluster for depth */}
          <filter id={`clusterShadow-${id}`} x="-30%" y="-10%" width="160%" height="130%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.5" floodColor="rgba(0,0,0,0.35)" floodOpacity="1" />
          </filter>
        </defs>
        {/* Curved stem with two vine leaves */}
        <g className="text-leaf-800" strokeLinecap="round" strokeLinejoin="round">
          <path
            d="M 28 1.5 Q 24 4 26 7 Q 28 9 28 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
          />
          {/* Left leaf: lobed grape leaf with serrated edges */}
          <path
            d="M 26 6 Q 20 3 14 5 Q 11 7 10 9 L 9 10 Q 8 12 10 13 L 9 14 Q 11 15 15 14 Q 18 13 21 13 L 23 12 Q 26 9 26 6 Z"
            {...leafPathProps}
          />
          {/* Right leaf: lobed grape leaf with serrated edges (mirror) */}
          <path
            d="M 30 6 Q 36 3 42 5 Q 45 7 46 9 L 47 10 Q 48 12 46 13 L 47 14 Q 45 15 41 14 Q 38 13 35 13 L 33 12 Q 30 9 30 6 Z"
            {...leafPathProps}
          />
        </g>
        {/* Whole cluster ripens together; base = currentColor (animates), overlay = 3D highlight, filter = shadow */}
        <g className="animate-grape-ripen" filter={`url(#clusterShadow-${id})`}>
          {GRAPE_COORDS.map(([cx, cy]) => (
            <g key={`${cx}-${cy}`}>
              <circle
                cx={cx}
                cy={cy}
                r="4.5"
                fill="currentColor"
                stroke="rgba(0,0,0,0.5)"
                strokeWidth="0.6"
              />
              <circle cx={cx} cy={cy} r="4.5" fill={`url(#grapeHighlight-${id})`} />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
});

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;
